import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { getUser } from "../graphql/queries";
import { updateUserProfile } from "../graphql/mutations";
import { useUser } from "../contexts/UserContext";
import { useUserPlan } from "../graphql/hooks";
import { ChangeLogChangeType, ChangeLogEntityType } from "../API";
import {
  type ChangeLogEntryInput,
  createChangeRequestId,
  logChanges,
} from "../graphql/fetchers";
import {
  BillingPaymentSummary,
  BillingPlan,
  BillingUser,
  formatBillingProviderLabel,
  getBillingPlan,
  getBillingLifecycleState,
  getBillingPlanTypeLabelKey,
  getBillingTimelineLabelKey,
  getLatestSuccessfulBillingPayment,
  getScheduledBillingPlan,
} from "../utilities/billing";
import {
  convertUtcToLocal,
  formatCurrency,
  formatPhoneToE164,
  getUserFriendlyErrorMessage,
  logToConsole,
} from "../utilities/utilities";
import { getLocalizedPlan } from "../utilities/publicContent";
import {
  FieldErrorMap,
  FieldValidators,
  phoneFieldValidator,
  requiredFieldValidator,
  trimFormValues,
  useUnsavedChangesModalGuard,
  validateFormFields,
} from "../utilities/formValidation";
import { useNavigationGuard } from "../contexts/NavigationGuardContext";
import FormFieldRow from "./FormFieldRow";
import ConfirmationModal from "../components/ui/ConfirmationModal";
import BlockingPageLoader from "./ui/BlockingPageLoader";
import "../styleSheets/profile.css";
import "../styleSheets/buttons.css";
import "../styleSheets/commonElements.css";
import "../styleSheets/formFields.css";

type ProfileFormValues = {
  firstName: string;
  lastName: string;
  preferredName: string;
  dateOfBirth: string;
  primaryPhone: string;
  primaryPhoneType: string;
  secondaryPhone: string;
  secondaryPhoneType: string;
};

type ProfileAuditSnapshot = {
  firstName: string | null;
  lastName: string | null;
  preferredName: string | null;
  dateOfBirth: string | null;
  primaryPhone: string | null;
  primaryPhoneType: string | null;
  secondaryPhone: string | null;
  secondaryPhoneType: string | null;
};

type ProfileChangedFields = {
  firstName?: { old: string | null; new: string | null };
  lastName?: { old: string | null; new: string | null };
  preferredName?: { old: string | null; new: string | null };
  dateOfBirth?: { old: string | null; new: string | null };
  primaryPhone?: { old: string | null; new: string | null };
  primaryPhoneType?: { old: string | null; new: string | null };
  secondaryPhone?: { old: string | null; new: string | null };
  secondaryPhoneType?: { old: string | null; new: string | null };
};

const client = generateClient();

const emptyForm: ProfileFormValues = {
  firstName: "",
  lastName: "",
  preferredName: "",
  dateOfBirth: "",
  primaryPhone: "",
  primaryPhoneType: "",
  secondaryPhone: "",
  secondaryPhoneType: "",
};

const PROFILE_CHANGE_LOG_METADATA = {
  source: "UI",
  page: "Profile",
  device: "web",
} as const;

function toNullable(value: string) {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function normalizeProfileAuditValue(
  value: string | null | undefined,
): string | null {
  const trimmedValue = (value ?? "").trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
}

function normalizeProfileAuditPhone(
  value: string | null | undefined,
): string | null {
  const normalizedValue = normalizeProfileAuditValue(value);
  if (!normalizedValue) return null;

  const formattedPhone = formatPhoneToE164(normalizedValue);
  return formattedPhone ?? normalizedValue;
}

function getProfileSnapshot(
  values: Partial<ProfileFormValues> | null | undefined,
): ProfileAuditSnapshot {
  return {
    firstName: normalizeProfileAuditValue(values?.firstName),
    lastName: normalizeProfileAuditValue(values?.lastName),
    preferredName: normalizeProfileAuditValue(values?.preferredName),
    dateOfBirth: normalizeProfileAuditValue(values?.dateOfBirth),
    primaryPhone: normalizeProfileAuditPhone(values?.primaryPhone),
    primaryPhoneType: normalizeProfileAuditValue(values?.primaryPhoneType),
    secondaryPhone: normalizeProfileAuditPhone(values?.secondaryPhone),
    secondaryPhoneType: normalizeProfileAuditValue(values?.secondaryPhoneType),
  };
}

function getChangedFields(
  before: ProfileAuditSnapshot,
  after: ProfileAuditSnapshot,
): ProfileChangedFields {
  const changedFields: ProfileChangedFields = {};

  if (before.firstName !== after.firstName) {
    changedFields.firstName = { old: before.firstName, new: after.firstName };
  }

  if (before.lastName !== after.lastName) {
    changedFields.lastName = { old: before.lastName, new: after.lastName };
  }

  if (before.preferredName !== after.preferredName) {
    changedFields.preferredName = {
      old: before.preferredName,
      new: after.preferredName,
    };
  }

  if (before.dateOfBirth !== after.dateOfBirth) {
    changedFields.dateOfBirth = {
      old: before.dateOfBirth,
      new: after.dateOfBirth,
    };
  }

  if (before.primaryPhone !== after.primaryPhone) {
    changedFields.primaryPhone = {
      old: before.primaryPhone,
      new: after.primaryPhone,
    };
  }

  if (before.primaryPhoneType !== after.primaryPhoneType) {
    changedFields.primaryPhoneType = {
      old: before.primaryPhoneType,
      new: after.primaryPhoneType,
    };
  }

  if (before.secondaryPhone !== after.secondaryPhone) {
    changedFields.secondaryPhone = {
      old: before.secondaryPhone,
      new: after.secondaryPhone,
    };
  }

  if (before.secondaryPhoneType !== after.secondaryPhoneType) {
    changedFields.secondaryPhoneType = {
      old: before.secondaryPhoneType,
      new: after.secondaryPhoneType,
    };
  }

  return changedFields;
}

function formatDateTime(value?: string | null): string | null {
  const normalizedValue = (value ?? "").trim();
  if (!normalizedValue) return null;

  const parsed = new Date(normalizedValue);
  return Number.isNaN(parsed.getTime())
    ? normalizedValue
    : parsed.toLocaleString();
}

function formatDisplayDate(value?: string | null): string | null {
  const normalizedValue = (value ?? "").trim();
  if (!normalizedValue) return null;

  return (
    convertUtcToLocal(normalizedValue, "DateOnlyWithDay") ||
    formatDateTime(normalizedValue)
  );
}

function formatPaymentAmount(
  amount?: number | null,
  currency?: string | null,
): string | null {
  if (typeof amount !== "number" || Number.isNaN(amount)) {
    return null;
  }

  const normalizedCurrency = (currency ?? "").trim().toUpperCase();
  if (normalizedCurrency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: normalizedCurrency,
      }).format(amount);
    } catch {
      return `${formatCurrency(amount)} ${normalizedCurrency}`;
    }
  }

  return formatCurrency(amount);
}

function Profile() {
  const navigate = useNavigate();
  const { setGuard } = useNavigationGuard();
  const { t } = useTranslation("common");
  const { user, isAuthenticated, refreshUserProfile } = useUser();
  const userId = user?.userId ?? "";

  const [profileId, setProfileId] = useState<string>("");
  const [registeredEmail, setRegisteredEmail] = useState<string>("");
  const [billingProvider, setBillingProvider] = useState<string>("");
  const [billingUserState, setBillingUserState] = useState<BillingUser | null>(
    null,
  );
  const [scheduledPlan, setScheduledPlan] = useState<BillingPlan | null>(null);
  const [pendingPlan, setPendingPlan] = useState<BillingPlan | null>(null);
  const [lastSuccessfulPayment, setLastSuccessfulPayment] =
    useState<BillingPaymentSummary | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string>("");

  const [formValues, setFormValues] = useState<ProfileFormValues>(emptyForm);
  const [initialValues, setInitialValues] = useState<ProfileFormValues | null>(
    null,
  );
  const auditBaselineSnapshotRef = useRef<ProfileAuditSnapshot | null>(null);
  const [fieldErrors, setFieldErrors] = useState<
    FieldErrorMap<ProfileFormValues>
  >({});

  const [isSaveResultModalOpen, setIsSaveResultModalOpen] = useState(false);
  const [saveResultTitle, setSaveResultTitle] = useState("");
  const [saveResultMessage, setSaveResultMessage] = useState("");
  const [saveShouldReturnToDashboard, setSaveShouldReturnToDashboard] =
    useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string>("");

  const planQ = useUserPlan(userId, isAuthenticated && !!userId);
  const userPlan = planQ.data;
  const billingState = useMemo(
    () => getBillingLifecycleState(billingUserState),
    [billingUserState],
  );
  const billingEffectiveAtLabel = useMemo(
    () => formatDisplayDate(billingState.billingEffectiveAt),
    [billingState.billingEffectiveAt],
  );
  const normalizedBillingProvider = useMemo(
    () =>
      formatBillingProviderLabel(billingProvider) ??
      t("profile.noBillingProvider"),
    [billingProvider, t],
  );
  const currentPlanTypeLabel = useMemo(
    () => (userPlan ? t(getBillingPlanTypeLabelKey(userPlan)) : "N/A"),
    [t, userPlan],
  );
  const pendingPlanTypeLabel = useMemo(
    () => (pendingPlan ? t(getBillingPlanTypeLabelKey(pendingPlan)) : "N/A"),
    [pendingPlan, t],
  );
  const billingTimelineLabelKey = useMemo(
    () =>
      getBillingTimelineLabelKey({ user: billingUserState, plan: userPlan }),
    [billingUserState, userPlan],
  );
  const lastPaymentDateLabel = useMemo(
    () => formatDisplayDate(lastSuccessfulPayment?.paymentDate),
    [lastSuccessfulPayment?.paymentDate],
  );
  const lastPaymentAmountLabel = useMemo(
    () =>
      formatPaymentAmount(
        lastSuccessfulPayment?.amount,
        lastSuccessfulPayment?.currency,
      ),
    [lastSuccessfulPayment?.amount, lastSuccessfulPayment?.currency],
  );
  const pendingPaymentAmountLabel = useMemo(() => {
    if (
      typeof billingUserState?.pendingPaymentAmount !== "number" ||
      Number.isNaN(billingUserState.pendingPaymentAmount)
    ) {
      return null;
    }

    return formatCurrency(billingUserState.pendingPaymentAmount);
  }, [billingUserState?.pendingPaymentAmount]);
  const localizedPendingPlan = useMemo(
    () => (pendingPlan ? getLocalizedPlan(pendingPlan, t) : null),
    [pendingPlan, t],
  );
  const hasPendingPaidSelection = useMemo(() => {
    const currentPlanId = (billingUserState?.userPlanId ?? "").trim();
    const pendingPlanId = (billingUserState?.pendingPlanId ?? "").trim();

    if (!pendingPlanId || pendingPlanId === currentPlanId || !pendingPlan) {
      return false;
    }

    return Number(pendingPlan.price ?? 0) > 0;
  }, [
    billingUserState?.pendingPlanId,
    billingUserState?.userPlanId,
    pendingPlan,
  ]);
  const showPendingPaymentAction =
    hasPendingPaidSelection && billingState.hasPendingCheckout;
  const scheduledChangeMessage = useMemo(() => {
    if (!billingState.hasScheduledDowngrade) return null;

    const targetPlanName =
      scheduledPlan?.name ??
      billingUserState?.scheduledPlanId ??
      t("profile.selectedPlanFallback");

    return billingEffectiveAtLabel
      ? t("checkout.onScreenMessage.scheduledChangeOn", {
          planName: targetPlanName,
          dateTime: billingEffectiveAtLabel,
        })
      : t("checkout.onScreenMessage.scheduledChangeAtPeriodEnd", {
          planName: targetPlanName,
        });
  }, [
    billingEffectiveAtLabel,
    billingState.hasScheduledDowngrade,
    billingUserState?.scheduledPlanId,
    scheduledPlan?.name,
    t,
  ]);
  const paymentFailureMessage = useMemo(() => {
    if (!billingState.hasPaymentFailure) return null;

    return billingEffectiveAtLabel
      ? t("profile.paymentFailedUntil", { date: billingEffectiveAtLabel })
      : t("profile.paymentFailedGeneric");
  }, [billingEffectiveAtLabel, billingState.hasPaymentFailure, t]);
  const canceledMessage = useMemo(() => {
    if (!billingState.isCanceledAtPeriodEnd) return null;

    return billingEffectiveAtLabel
      ? t("profile.subscriptionCanceledUntil", {
          date: billingEffectiveAtLabel,
        })
      : t("profile.subscriptionCanceledGeneric");
  }, [billingEffectiveAtLabel, billingState.isCanceledAtPeriodEnd, t]);
  const billingStatusMessage = paymentFailureMessage ?? canceledMessage;
  const billingNotices = useMemo(() => {
    const notices: Array<{ tone: "warning" | "info"; message: string }> = [];

    if (paymentFailureMessage) {
      notices.push({
        tone: "warning",
        message: paymentFailureMessage,
      });
    }

    if (canceledMessage) {
      notices.push({
        tone: "info",
        message: canceledMessage,
      });
    }

    return notices;
  }, [canceledMessage, paymentFailureMessage]);

  const normalizedValues = useMemo(
    () => trimFormValues(formValues),
    [formValues],
  );
  const hasUnsavedChanges = useMemo(() => {
    if (!initialValues) return false;
    return JSON.stringify(normalizedValues) !== JSON.stringify(initialValues);
  }, [initialValues, normalizedValues]);

  const {
    isPromptOpen: showUnsavedDialog,
    requestNavigation,
    confirmNavigation,
    cancelNavigation: handleContinueEditing,
  } = useUnsavedChangesModalGuard(hasUnsavedChanges);

  useEffect(() => {
    setGuard(hasUnsavedChanges ? requestNavigation : null);
    return () => setGuard(null);
  }, [hasUnsavedChanges, requestNavigation, setGuard]);

  const PROFILE_VALIDATORS: FieldValidators<ProfileFormValues> = {
    firstName: [requiredFieldValidator(t("profile.validation.fNameReq"))],
    lastName: [requiredFieldValidator(t("profile.validation.lNameReq"))],
    preferredName: [requiredFieldValidator(t("profile.validation.pNameReq"))],
    primaryPhone: [
      requiredFieldValidator(t("profile.validation.phoneReq")),
      phoneFieldValidator(t("profile.validation.phoneValid")),
    ],
    primaryPhoneType: [
      requiredFieldValidator(t("profile.validation.phoneTypeReq")),
    ],
  };

  useEffect(() => {
    const loadProfile = async () => {
      if (!isAuthenticated || !userId) return;

      setIsLoading(true);
      setLoadError("");
      setLastSuccessfulPayment(null);

      try {
        const result: any = await client.graphql({
          query: getUser,
          variables: { id: userId },
        });

        const currentUser = result?.data?.getUser;
        const userProfile = currentUser?.UserProfile;

        if (!currentUser || !userProfile) {
          setLoadError(t("profile.errorLoadingProfile"));
          return;
        }

        const fetchedValues: ProfileFormValues = {
          firstName: userProfile.firstName ?? "",
          lastName: userProfile.lastName ?? "",
          preferredName: userProfile.preferredName ?? "",
          dateOfBirth: userProfile.dateOfBirth ?? "",
          primaryPhone: userProfile.primaryPhone ?? "",
          primaryPhoneType: userProfile.primaryPhoneType ?? "",
          secondaryPhone: userProfile.secondaryPhone ?? "",
          secondaryPhoneType: userProfile.secondaryPhoneType ?? "",
        };

        let latestPayment: BillingPaymentSummary | null = null;
        try {
          latestPayment = await getLatestSuccessfulBillingPayment(userId);
        } catch (paymentError) {
          logToConsole(
            "warn",
            "Unable to load latest successful billing payment:",
            paymentError,
          );
        }

        const normalizedFetchedValues = trimFormValues(fetchedValues);
        auditBaselineSnapshotRef.current = getProfileSnapshot(fetchedValues);
        setProfileId(userProfile.id);
        setFormValues(normalizedFetchedValues);
        setInitialValues(normalizedFetchedValues);
        setRegisteredEmail(
          (currentUser.email ?? user?.signInDetails?.loginId ?? "").trim(),
        );
        setBillingProvider((currentUser.billingProvider ?? "").trim());
        setBillingUserState(currentUser as BillingUser);
        setLastSuccessfulPayment(latestPayment);
        setLastUpdatedAt(userProfile.updatedAt ?? "");
        setFieldErrors({});
      } catch (error) {
        logToConsole("error", "Error loading profile:", error);
        setLoadError(getUserFriendlyErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    };

    void loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.signInDetails?.loginId, userId]);

  useEffect(() => {
    let isCancelled = false;

    const loadScheduledPlan = async () => {
      const scheduledPlanId = (billingUserState?.scheduledPlanId ?? "").trim();
      if (!scheduledPlanId) {
        setScheduledPlan(null);
        return;
      }

      try {
        const plan = await getScheduledBillingPlan(billingUserState);
        if (!isCancelled) {
          setScheduledPlan(plan);
        }
      } catch (error) {
        if (!isCancelled) {
          logToConsole("warn", "Unable to load scheduled billing plan:", error);
          setScheduledPlan(null);
        }
      }
    };

    void loadScheduledPlan();

    return () => {
      isCancelled = true;
    };
  }, [billingUserState]);

  useEffect(() => {
    let isCancelled = false;

    const loadPendingPlan = async () => {
      const pendingPlanId = (billingUserState?.pendingPlanId ?? "").trim();
      const currentPlanId = (billingUserState?.userPlanId ?? "").trim();

      if (!pendingPlanId || pendingPlanId === currentPlanId) {
        setPendingPlan(null);
        return;
      }

      try {
        const plan = await getBillingPlan(pendingPlanId);
        if (!isCancelled) {
          setPendingPlan(plan);
        }
      } catch (error) {
        if (!isCancelled) {
          logToConsole("warn", "Unable to load pending billing plan:", error);
          setPendingPlan(null);
        }
      }
    };

    void loadPendingPlan();

    return () => {
      isCancelled = true;
    };
  }, [billingUserState?.pendingPlanId, billingUserState?.userPlanId]);

  const handleFieldChange = (
    fieldName: keyof ProfileFormValues,
    value: string,
  ) => {
    setFormValues((prev) => ({ ...prev, [fieldName]: value }));

    if (fieldErrors[fieldName]) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: undefined }));
    }
  };

  const handleSaveProfile = async () => {
    if (!profileId) return;

    if (!hasUnsavedChanges) {
      setSaveShouldReturnToDashboard(true);
      setSaveResultTitle(t("errorMessage.noChangesTitle"));
      setSaveResultMessage(t("errorMessage.noChangesToSave"));
      setIsSaveResultModalOpen(true);
      return;
    }

    const trimmedValues = trimFormValues(formValues);
    const validationErrors = validateFormFields(
      trimmedValues,
      PROFILE_VALIDATORS,
    );
    setFieldErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSaving(true);

    try {
      const beforeAuditSnapshot = auditBaselineSnapshotRef.current;
      const updateRequestId = createChangeRequestId();
      const updateAuditEntries: ChangeLogEntryInput[] = [
        {
          requestId: updateRequestId,
          entityType: ChangeLogEntityType.PROFILE,
          entityId: profileId,
          changeType: ChangeLogChangeType.UPDATE,
          metadata: PROFILE_CHANGE_LOG_METADATA,
          userId,
        },
      ];
      const savedProfileValues: ProfileFormValues = {
        firstName: trimmedValues.firstName,
        lastName: trimmedValues.lastName,
        preferredName: trimmedValues.preferredName,
        dateOfBirth: toNullable(trimmedValues.dateOfBirth) ?? "",
        primaryPhone: formatPhoneToE164(trimmedValues.primaryPhone) ?? "",
        primaryPhoneType: trimmedValues.primaryPhoneType,
        secondaryPhone: trimmedValues.secondaryPhone
          ? (formatPhoneToE164(trimmedValues.secondaryPhone) ?? "")
          : "",
        secondaryPhoneType: toNullable(trimmedValues.secondaryPhoneType) ?? "",
      };

      await client.graphql({
        query: updateUserProfile,
        variables: {
          input: {
            id: profileId,
            firstName: trimmedValues.firstName,
            lastName: trimmedValues.lastName,
            preferredName: trimmedValues.preferredName,
            dateOfBirth: toNullable(trimmedValues.dateOfBirth),
            primaryPhone: formatPhoneToE164(trimmedValues.primaryPhone),
            primaryPhoneType: trimmedValues.primaryPhoneType,
            secondaryPhone: trimmedValues.secondaryPhone
              ? formatPhoneToE164(trimmedValues.secondaryPhone)
              : null,
            secondaryPhoneType: toNullable(trimmedValues.secondaryPhoneType),
          },
        },
      });

      if (beforeAuditSnapshot) {
        const afterAuditSnapshot = getProfileSnapshot(savedProfileValues);
        const changedFields = getChangedFields(
          beforeAuditSnapshot,
          afterAuditSnapshot,
        );

        if (changedFields.firstName) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.PROFILE,
            entityId: profileId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "firstName",
            oldValue: changedFields.firstName.old,
            newValue: changedFields.firstName.new,
            metadata: PROFILE_CHANGE_LOG_METADATA,
            userId,
          });
        }

        if (changedFields.lastName) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.PROFILE,
            entityId: profileId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "lastName",
            oldValue: changedFields.lastName.old,
            newValue: changedFields.lastName.new,
            metadata: PROFILE_CHANGE_LOG_METADATA,
            userId,
          });
        }

        if (changedFields.preferredName) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.PROFILE,
            entityId: profileId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "preferredName",
            oldValue: changedFields.preferredName.old,
            newValue: changedFields.preferredName.new,
            metadata: PROFILE_CHANGE_LOG_METADATA,
            userId,
          });
        }

        if (changedFields.dateOfBirth) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.PROFILE,
            entityId: profileId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "dateOfBirth",
            oldValue: changedFields.dateOfBirth.old,
            newValue: changedFields.dateOfBirth.new,
            metadata: PROFILE_CHANGE_LOG_METADATA,
            userId,
          });
        }

        if (changedFields.primaryPhone) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.PROFILE,
            entityId: profileId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "primaryPhone",
            oldValue: changedFields.primaryPhone.old,
            newValue: changedFields.primaryPhone.new,
            metadata: PROFILE_CHANGE_LOG_METADATA,
            userId,
          });
        }

        if (changedFields.primaryPhoneType) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.PROFILE,
            entityId: profileId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "primaryPhoneType",
            oldValue: changedFields.primaryPhoneType.old,
            newValue: changedFields.primaryPhoneType.new,
            metadata: PROFILE_CHANGE_LOG_METADATA,
            userId,
          });
        }

        if (changedFields.secondaryPhone) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.PROFILE,
            entityId: profileId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "secondaryPhone",
            oldValue: changedFields.secondaryPhone.old,
            newValue: changedFields.secondaryPhone.new,
            metadata: PROFILE_CHANGE_LOG_METADATA,
            userId,
          });
        }

        if (changedFields.secondaryPhoneType) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.PROFILE,
            entityId: profileId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "secondaryPhoneType",
            oldValue: changedFields.secondaryPhoneType.old,
            newValue: changedFields.secondaryPhoneType.new,
            metadata: PROFILE_CHANGE_LOG_METADATA,
            userId,
          });
        }

        auditBaselineSnapshotRef.current = afterAuditSnapshot;
      }

      void logChanges(updateAuditEntries).catch(() => undefined);

      setFormValues(trimmedValues);
      setInitialValues(trimmedValues);
      setLastUpdatedAt(new Date().toISOString());
      setFieldErrors({});
      await refreshUserProfile();

      setSaveShouldReturnToDashboard(true);
      setSaveResultTitle(t("profile.validation.updateSuccessTitle"));
      setSaveResultMessage(t("profile.validation.updateSuccess"));
      setIsSaveResultModalOpen(true);
    } catch (error) {
      logToConsole("error", "Error saving profile:", error);

      setSaveShouldReturnToDashboard(false);
      setSaveResultTitle(t("profile.validation.updateFailedTitle"));
      setSaveResultMessage(t("profile.validation.updateFailed"));
      setIsSaveResultModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDiscardChanges = () => {
    if (hasUnsavedChanges && profileId) {
      void logChanges([
        {
          requestId: createChangeRequestId(),
          entityType: ChangeLogEntityType.PROFILE,
          entityId: profileId,
          changeType: ChangeLogChangeType.ABANDON,
          metadata: PROFILE_CHANGE_LOG_METADATA,
          userId,
        },
      ]).catch(() => undefined);
    }

    confirmNavigation();
  };

  const handleChangePlan = () => {
    requestNavigation(() => navigate("/Plans"));
  };

  const handlePayPendingPlan = () => {
    const pendingPlanId = (billingUserState?.pendingPlanId ?? "").trim();
    if (!pendingPlanId) return;

    requestNavigation(() =>
      navigate(`/Checkout?planId=${encodeURIComponent(pendingPlanId)}`),
    );
  };

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/"
        replace
        state={{ toast: { type: "info", message: t("common.signIn") } }}
      />
    );
  }

  if (isLoading) {
    return <BlockingPageLoader message={t("profile.loading")} />;
  }

  if (isSaving) {
    return <BlockingPageLoader message={t("common.saving")} />;
  }

  if (loadError) {
    return (
      <div className="profile-page">
        <div className="profile-content">
          <div className="profile-banner profile-banner--error">
            {loadError}
          </div>
        </div>
      </div>
    );
  }

  const planPrice = Number(userPlan?.price ?? 0);
  const handleCancel = () => {
    requestNavigation(() => navigate("/Dashboard"));
  };
  const handleContinueEditingAfterSave = () => {
    setIsSaveResultModalOpen(false);
  };

  const handleFinishEditingAfterSave = () => {
    setIsSaveResultModalOpen(false);
    if (saveShouldReturnToDashboard) {
      navigate("/Dashboard");
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-topbar">
        <div className="profile-topbar__nav">
          <div className="profile-topbar__container">
            <div className="profile-topbar__row">
              <button
                type="button"
                className="btn btn--gray btn--size-md btn--icon-left"
                onClick={handleCancel}
              >
                <div
                  className="btn__icon icon-chevron-left"
                  aria-hidden="true"
                />
                {t("common.cancel")}
              </button>

              <div className="profile-topbar__title">
                {t("header.menu.profile")}
              </div>
              <div className="profile-topbar__actions">
                <button
                  type="button"
                  className="btn btn--green btn--size-md btn--icon-left"
                  onClick={handleSaveProfile}
                  disabled={isSaving}
                >
                  <span className="btn__icon icon-check" aria-hidden="true" />
                  {isSaving ? t("common.saving") : t("common.save")}
                </button>
              </div>
            </div>
            <div className="profile-topbar__meta">
              {lastUpdatedAt
                ? t("common.lastUpdated") +
                  ": " +
                  convertUtcToLocal(lastUpdatedAt, "DateOnly")
                : ""}
            </div>

            <div className="profile-topbar__titleBelow">
              <div className="profile-topbar__titleBelowMain">
                {t("header.menu.profile")}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="profile-content">
        <section className="profile-introCard">
          <div className="profile-introCard__title">
            {t("profile.yourProfile")}
          </div>
          <p className="profile-introCard__text">
            {t("profile.profileSubText")}
          </p>
        </section>

        <section className="profile-sectionCard">
          <div className="profile-sectionCard__title">
            {t("profile.profileInfo")}
          </div>

          <div className="profile-grid">
            <FormFieldRow
              id="firstName"
              label={t("header.register.fName")}
              required
              isWritten={normalizedValues.firstName.length > 0}
              error={fieldErrors.firstName}
            >
              <input
                id="firstName"
                className="profile-input"
                type="text"
                value={formValues.firstName}
                autoComplete="given-name"
                onChange={(event) =>
                  handleFieldChange("firstName", event.target.value)
                }
              />
            </FormFieldRow>

            <FormFieldRow
              id="lastName"
              label={t("header.register.lName")}
              required
              isWritten={normalizedValues.lastName.length > 0}
              error={fieldErrors.lastName}
            >
              <input
                id="lastName"
                className="profile-input"
                type="text"
                value={formValues.lastName}
                autoComplete="family-name"
                onChange={(event) =>
                  handleFieldChange("lastName", event.target.value)
                }
              />
            </FormFieldRow>

            <FormFieldRow
              id="preferredName"
              label={t("header.register.pName")}
              required
              isWritten={normalizedValues.preferredName.length > 0}
              error={fieldErrors.preferredName}
            >
              <input
                id="preferredName"
                className="profile-input"
                type="text"
                value={formValues.preferredName}
                autoComplete="nickname"
                onChange={(event) =>
                  handleFieldChange("preferredName", event.target.value)
                }
              />
            </FormFieldRow>

            <FormFieldRow
              id="dateOfBirth"
              label={t("profile.birthDate")}
              isWritten={normalizedValues.dateOfBirth.length > 0}
              error={fieldErrors.dateOfBirth}
            >
              <input
                id="dateOfBirth"
                className="profile-input"
                type="date"
                value={formValues.dateOfBirth}
                autoComplete="bday"
                onChange={(event) =>
                  handleFieldChange("dateOfBirth", event.target.value)
                }
              />
            </FormFieldRow>

            <FormFieldRow
              id="primaryPhone"
              label={t("profile.primaryPhone")}
              required
              isWritten={normalizedValues.primaryPhone.length > 0}
              error={fieldErrors.primaryPhone}
            >
              <input
                id="primaryPhone"
                className="profile-input"
                type="tel"
                value={formValues.primaryPhone}
                autoComplete="tel"
                onChange={(event) =>
                  handleFieldChange("primaryPhone", event.target.value)
                }
              />
            </FormFieldRow>

            <FormFieldRow
              id="primaryPhoneType"
              label={t("profile.primaryPhoneType")}
              required
              isWritten={normalizedValues.primaryPhoneType.length > 0}
              error={fieldErrors.primaryPhoneType}
            >
              <select
                id="primaryPhoneType"
                className="profile-select"
                value={formValues.primaryPhoneType}
                onChange={(event) =>
                  handleFieldChange("primaryPhoneType", event.target.value)
                }
              >
                <option value="">Select type</option>
                <option value="mobile">Mobile</option>
                <option value="home">Home</option>
                <option value="work">Work</option>
              </select>
            </FormFieldRow>

            <FormFieldRow
              id="secondaryPhone"
              label={t("profile.secondaryPhone")}
              isWritten={normalizedValues.secondaryPhone.length > 0}
              error={fieldErrors.secondaryPhone}
            >
              <input
                id="secondaryPhone"
                className="profile-input"
                type="tel"
                value={formValues.secondaryPhone}
                autoComplete="tel"
                onChange={(event) =>
                  handleFieldChange("secondaryPhone", event.target.value)
                }
              />
            </FormFieldRow>

            <FormFieldRow
              id="secondaryPhoneType"
              label={t("profile.secondaryPhoneType")}
              isWritten={normalizedValues.secondaryPhoneType.length > 0}
              error={fieldErrors.secondaryPhoneType}
            >
              <select
                id="secondaryPhoneType"
                className="profile-select"
                value={formValues.secondaryPhoneType}
                onChange={(event) =>
                  handleFieldChange("secondaryPhoneType", event.target.value)
                }
              >
                <option value="">Select type</option>
                <option value="mobile">Mobile</option>
                <option value="home">Home</option>
                <option value="work">Work</option>
              </select>
            </FormFieldRow>
          </div>
        </section>

        <section className="profile-sectionCard">
          <div className="profile-sectionCard__title">
            {t("profile.registeredEmail")}
          </div>

          <FormFieldRow
            id="profileEmail"
            label={t("header.register.email")}
            isWritten={registeredEmail.length > 0}
            readOnly
          >
            <div id="profileEmail" className="profile-emailValue">
              {registeredEmail || t("profile.noEmail")}
            </div>
          </FormFieldRow>

          <div className="profile-emailNotice">
            {t("profile.noEmailChanges")}
          </div>
        </section>

        <section className="profile-sectionCard">
          <div className="profile-sectionHeader">
            <div className="profile-sectionCard__title">
              {t("pageBody.plans.currentPlan")}
            </div>

            {!showPendingPaymentAction ? (
              <button
                type="button"
                className="btn btn--white btn--size-md"
                onClick={handleChangePlan}
              >
                {t("checkout.plans.change")}
              </button>
            ) : null}
          </div>

          {billingNotices.length > 0 ? (
            <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
              {billingNotices.map((notice) => (
                <div
                  key={notice.message}
                  className={`profile-banner ${
                    notice.tone === "warning"
                      ? "profile-banner--error"
                      : "profile-banner--success"
                  }`}
                >
                  {notice.message}
                </div>
              ))}
            </div>
          ) : null}

          {planQ.isLoading ? (
            <div>{t("checkout.plans.loading")}</div>
          ) : planQ.error ? (
            <div className="profile-banner profile-banner--error">
              {t("checkout.plans.unableLoad")}
            </div>
          ) : userPlan ? (
            <>
              <div className="profile-planGrid">
                <div className="profile-planGrid__item">
                  <div className="profile-planGrid__label">
                    {t("profile.planName")}
                  </div>
                  <div className="profile-planGrid__value">
                    {userPlan.name ?? "N/A"}
                  </div>
                </div>
                <div className="profile-planGrid__item">
                  <div className="profile-planGrid__label">
                    {t("profile.planType")}
                  </div>
                  <div className="profile-planGrid__value">
                    {currentPlanTypeLabel}
                  </div>
                </div>
                <div className="profile-planGrid__item">
                  <div className="profile-planGrid__label">
                    {t("profile.planPrice")}
                  </div>
                  <div className="profile-planGrid__value">
                    {formatCurrency(planPrice)}
                  </div>
                </div>
                <div className="profile-planGrid__item">
                  <div className="profile-planGrid__label">
                    {t("profile.planBillingCycle")}
                  </div>
                  <div className="profile-planGrid__value">
                    {userPlan.billingCycle ?? "N/A"}
                  </div>
                </div>
                <div className="profile-planGrid__item">
                  <div className="profile-planGrid__label">
                    {t("profile.allowedMessages")}
                  </div>
                  <div className="profile-planGrid__value">
                    {!userPlan.messagesAllowed
                      ? 0
                      : userPlan.messagesAllowed === 9999
                        ? "Unlimited"
                        : userPlan.messagesAllowed}
                  </div>
                </div>
                <div className="profile-planGrid__item">
                  <div className="profile-planGrid__label">
                    {t("profile.allowedRecipients")}
                  </div>
                  <div className="profile-planGrid__value">
                    {!userPlan.recipientsAllowed
                      ? 0
                      : userPlan.recipientsAllowed === 9999
                        ? "Unlimited"
                        : userPlan.recipientsAllowed}
                  </div>
                </div>
                <div className="profile-planGrid__item">
                  <div className="profile-planGrid__label">
                    {t("profile.allowedFiles")}
                  </div>
                  <div className="profile-planGrid__value">
                    {userPlan.attachmentsAllowed ?? 0}
                  </div>
                </div>
              </div>

              <div className="profile-sectionDivider" />
              <div className="profile-sectionHeader">
                <div className="profile-sectionSubTitle">
                  {t("profile.billingDetails")}
                </div>
                {showPendingPaymentAction ? (
                  <button
                    type="button"
                    className="btn btn--white btn--size-md"
                    onClick={handlePayPendingPlan}
                  >
                    {t("checkout.pay")}
                  </button>
                ) : null}
              </div>

              <div className="profile-planGrid">
                {hasPendingPaidSelection ? (
                  <div className="profile-planGrid__item">
                    <div className="profile-planGrid__label">
                      {t("checkout.selected")}
                    </div>
                    <div className="profile-planGrid__valueStack">
                      <span className="profile-planGrid__value">
                        {localizedPendingPlan?.displayName ??
                          pendingPlan?.name ??
                          billingUserState?.pendingPlanId ??
                          "N/A"}
                      </span>
                      <span className="profile-planGrid__valueSecondary">
                        {pendingPlanTypeLabel}
                      </span>
                    </div>
                  </div>
                ) : null}

                <div className="profile-planGrid__item">
                  <div className="profile-planGrid__label">
                    {hasPendingPaidSelection
                      ? t("profile.estimatedPendingAmount")
                      : t("profile.lastPayment")}
                  </div>
                  <div className="profile-planGrid__valueStack">
                    <span className="profile-planGrid__value">
                      {hasPendingPaidSelection
                        ? (pendingPaymentAmountLabel ??
                          t("profile.estimatedPendingAmountUnavailable"))
                        : `${t("profile.lastPaymentDate")} ${
                            lastPaymentDateLabel ??
                            t("profile.lastPaymentDateUnavailable")
                          }`}
                    </span>
                    {!hasPendingPaidSelection ? (
                      <span className="profile-planGrid__valueSecondary">
                        {`${t("profile.lastPaymentAmount")} ${
                          lastPaymentAmountLabel ??
                          t("profile.lastPaymentAmountUnavailable")
                        }`}
                      </span>
                    ) : null}
                  </div>
                </div>

                {billingTimelineLabelKey && billingEffectiveAtLabel ? (
                  <div className="profile-planGrid__item">
                    <div className="profile-planGrid__label">
                      {t(billingTimelineLabelKey)}
                    </div>
                    <div className="profile-planGrid__value">
                      {billingEffectiveAtLabel}
                    </div>
                  </div>
                ) : null}

                {scheduledChangeMessage ? (
                  <div className="profile-planGrid__item">
                    <div className="profile-planGrid__label">
                      {t("profile.scheduledChange")}
                    </div>
                    <div className="profile-planGrid__value">
                      {scheduledChangeMessage}
                    </div>
                  </div>
                ) : null}

                {billingStatusMessage ? (
                  <div className="profile-planGrid__item">
                    <div className="profile-planGrid__label">
                      {t("profile.lifecycleStatus")}
                    </div>
                    <div className="profile-planGrid__value">
                      {billingStatusMessage}
                    </div>
                  </div>
                ) : null}

                <div className="profile-planGrid__item">
                  <div className="profile-planGrid__label">
                    {t("profile.billingProvider")}
                  </div>
                  <div className="profile-planGrid__value">
                    {normalizedBillingProvider}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div>{t("profile.noActivePlan")}</div>
          )}
        </section>
      </div>
      <ConfirmationModal
        isOpen={showUnsavedDialog}
        title={t("errorMessage.unsavedChanges")}
        message={t("errorMessage.unsavedChangesPrompt")}
        primaryLabel={t("common.discardChanges")}
        secondaryLabel={t("common.continueEdit")}
        onPrimary={handleConfirmDiscardChanges}
        onSecondary={handleContinueEditing}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
      />
      <ConfirmationModal
        isOpen={isSaveResultModalOpen}
        title={saveResultTitle}
        message={saveResultMessage}
        primaryLabel={
          saveShouldReturnToDashboard
            ? t("common.finishEdit")
            : t("common.close")
        }
        secondaryLabel={t("common.continueEdit")}
        onPrimary={handleFinishEditingAfterSave}
        onSecondary={handleContinueEditingAfterSave}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
      />
    </div>
  );
}

export default Profile;
